import ReactPDF, { Image, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import React from "react";
import { getDateFormatter, isStringBlank, listMemberForId } from "../util/utils";
import { Timestamp } from "../components/Timestamp";
import vaultlogo from "../images/logos/vault_powered_pdf_gray.png";
import pdflogo from "../images/logos/applogo_pdf.png";
import nunitoSansRegular from "../images/fonts/NunitoSans-Regular.ttf";
import nunitoSansExtraBold from "../images/fonts/NunitoSans-ExtraBold.ttf";
import nunitoSansBold from "../images/fonts/NunitoSans-Bold.ttf";
import nunitoSansBlack from "../images/fonts/NunitoSans-Black.ttf";

import mcelroyLogo from "../images/mcelroy_logo.png";
import checkPassedMark from "../images/inspection_form/checkbox_pass.png";
import checkboxResolvedMark from "../images/inspection_form/checkbox_resolved.png";
import checkSkippedMark from "../images/inspection_form/checkbox_skipped.png";
import checkFailMark from "../images/inspection_form/checkbox_fail.png";
import checkNaMark from "../images/inspection_form/checkbox_na.png";
import legendImage from "../images/inspection_form/legend.png";
import { Api } from "../util/Api";

// May also want to look at jsPDF - https://stackoverflow.com/questions/44989119/generating-a-pdf-file-from-react-components

// NOTE - should this be done on client or server?

// NOTE - all fonts have to be explicitly defined.  Can't do Bold without a predefined font.
// https://react-pdf.org/fonts
Font.register({
	family: "Open Sans",
	fonts: [
		{ src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf' },
		{ src: 'https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-600.ttf', fontWeight: 600 }
	]
});
Font.register({
	family: "futura-pt",
	fonts: [
		{ src: nunitoSansRegular },
		{ src: nunitoSansBold, fontWeight: 600 },
		{ src: nunitoSansExtraBold, fontWeight: 800 },
		{ src: nunitoSansBlack, fontWeight: 900 }
	]
})

// Reference - https://react-pdf.org/styling
let styleBase = {
	/* General Helper Styles */
	definition: {
		borderBottom: "1pt solid black",
		paddingLeft: "8pt",
		display: "flex",
		flexDirection: "row"
	},
	detailDefinition: {
		display: "flex",
		flexDirection: "row",
		paddingLeft: "14pt",
	},
	key: {
		fontFamily: "futura-pt",
		fontSize: "8pt",
		fontWeight: "600",
	},
	value: {
		fontFamily: "futura-pt",
		fontSize: "8pt",
	},
	autogrow: {
		flexGrow: "1"
	},
	vertSpacer: {
		paddingBottom: "12pt",
	},

	/* Top-level Styles */
	pageStyle: {
		padding: "40pt"
	},
	inspection: {

	},

	/* Top Header */
	topHeader: {
		display: "flex",
		flexDirection: "row",
		marginBottom: "18pt"
	},
	logo: {
		width: "50%",
		marginRight: "0.5in"
	},
	topSpacer: {
		width: "10%"
	},
	dispositionBox: {
		flexGrow: "1",
		border: "1px solid black",
		display: "flex",
		flexDirection: "row"
	},
	dispositionDetails: {
		alignSelf: "center",
		flexGrow: "1"
	},
	result: {
		display: "flex",
		flexDirection: "row"
	},
	resultText: {
		color: "white",
		fontFamily: "futura-pt",
		fontSize: "20pt",
		fontWeight: "800",
		textAlign: "center",
		padding: "14pt",
		alignSelf: "center",
	},

	/* Sectioning Styles */
	generalBox: {
		marginBottom: "18pt"
	},
	horizontalBoxes: {
		display: "flex",
		flexDirection: "row",
		marginBottom: "18pt"
	},
	customerBox: {
		width: "60%",
		marginRight: "0.5in"
	},
	inspectorBox: {
		flexGrow: "1"
	},
	legend: {
		marginLeft: "5in",
		marginBottom: "6pt"
	},
	boxHeader: {
		fontFamily: "futura-pt",
		fontSize: "9pt",
		fontWeight: "800",
		color: "white",
		backgroundColor: "black",
		paddingLeft: "8pt",
		paddingTop: "3pt",
		paddingBottom: "3pt",
		marginBottom: "4pt"
	},
	userLogo: {
		marginTop: "6pt",
		maxHeight: "1.5in"
	},

	/* Check Styles */
	componentType: {
		fontFamily: "futura-pt",
		fontSize: "9pt",
		fontWeight: "800",
		paddingLeft: "8pt"
	},
	comments: {
		marginLeft: "14pt",
		fontFamily: "futura-pt",
		fontSize: "8pt",
	},
	questionShortContainer: {
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap"
	},
	questionShort: {
		width: "29%",
		display: "flex",
		flexDirection: "row",
		marginTop: "8pt",
		marginRight: "3%"
	},
	checkImage: {
		marginLeft: "14pt",
		width: "14pt",
		height: "14pt",
	},
	checkName: {
		fontFamily: "futura-pt",
		fontSize: "9pt",
		marginLeft: "6pt"
	},

	/* Image Styles */
	imageList: {
		marginLeft: "14pt",
		display: "flex",
		flexDirection: "row",
		flexWrap: "wrap",
		paddingBottom: "5pt"
	},
	photoContainer: {
	},
	photoImage: {
		display: "flex",
		maxHeight: "2in",
		maxWidth: "2in",
		marginRight: "10pt",
		marginBottom: "10pt",
		alignSelf: "center"
	},

	/* Table Styles */
	partTable: {
		padding: "5pt",
		marginBottom: "8pt",
	},
	partRowHeader: {
		display: "flex",
		flexDirection: "row"
	},
	partRow: {
		paddingTop: "4pt",
		display: "flex",
		flexDirection: "row",
		borderTop: "1pt solid #aaaaaa"
	},
	partNumber: {
		width: "1.5in"
	},
	partName: {
		width: "3in"
	},
	partNotes: {
		width: "2in"
	},
	partStatus: {
		width: "1in"
	},
	tblHdr: {
		fontFamily: "futura-pt",
		fontSize: "8pt",
		fontWeight: "800",
		paddingBottom: "2pt"
	},
	tblText: {
		fontFamily: "futura-pt",
		fontSize: "8pt",
		paddingBottom: "2pt"
	},

	/* Final Section */
	sigContainer: {
		display: "flex",
		flexDirection: "row",

	},
	sigImageContainer: {
		width: "50%",
	},
	sigImage: {
		display: "block",
		width: "100%",
		borderBottom: "1px solid black"
	},
	sigImageText: {
		fontFamily: "futura-pt",
		fontSize: "9pt",
		display: "block",
		width: "100%",
		borderTop: "1px solid black",
		paddingTop: "8pt",
		textAlign: "center"
	},
	vaultLogoContainer: {
		alignSelf: "center",
		width: "15%"
	},
	vaultLogo: {
		width: "100%"
	},

	/* Footer Definitions */
	footerLogo: {
		width: "1in",
		marginBottom: "8pt"
	},
	footerText: {
		fontFamily: "futura-pt",
		fontSize: "6pt",
	},
};

// Post-process similar styles
styleBase.resultPASS = Object.assign({backgroundColor: "#007041"}, styleBase.result);
styleBase.resultFAIL = Object.assign({backgroundColor: "#c50000"}, styleBase.result);

export let styles = StyleSheet.create(styleBase);


let checkImages = {
	"PASS": checkPassedMark,
	"FAIL": checkFailMark,
	"RESOLVED": checkboxResolvedMark,
	"NA": checkNaMark,
	"SKIP": checkSkippedMark
}

export function InspectionPDFRenderer(props) {
	let insp = props.inspection;
	let survey = listMemberForId(props.surveys, insp.surveyId);
	let job = listMemberForId(props.jobs, insp.jobId);
	let ctypes = props.component_types;
	let dFormatter = props.dateFormatter;
	if(dFormatter == null) {
		dFormatter = getDateFormatter("yyyy-MM-dd");
	}

	let contactDetailsPresent = (!!insp.companyName) || (!!insp.contactName) || (!!insp.contactPhone) || (!!insp.contactEmail) || (!!insp.contactAddress);

	let keyval = (k, v) => {
		return <View style={styles.definition}>
			<Text style={styles.key}>{k}: </Text>
			<Text style={styles.value}>{v}</Text>
		</View>
	};

	let keyvalDetail = (k, v) => {
		return <View style={styles.detailDefinition}>
			<Text style={styles.key}>{k}: </Text>
			<Text style={styles.value}>{v}</Text>
		</View>
	};

	let allQuestions = survey.surveyQuestions.concat(job?.jobQuestions || []);
	let qExtendedCount = 0;

	return <>
		<View style={styles.inspection}>
			<View style={styles.topHeader}>
				<Image style={styles.logo} src={pdflogo} />
				<View style={styles.topSpacer} />
				<View style={styles.dispositionBox}>
					<View style={styles.dispositionDetails}>
						{!!insp.disposition && keyvalDetail("Disposition", insp.disposition)}
						{!!insp.externalIdentifier && keyvalDetail("Sticker", insp.externalIdentifier)}
						{keyvalDetail("Date", <Timestamp dateString={insp.completedAt} formatter={dFormatter} />)}
						{!!insp.rentalDisposition && keyvalDetail("Rental", insp.rentalDisposition)}
					</View>
					<View style={styles["result" + insp.result]}><Text style={styles.resultText}>{insp.result}</Text></View>
				</View>
			</View>

			<View style={styles.horizontalBoxes}>
				<View style={styles.customerBox}>
					{contactDetailsPresent && <>
						<View style={styles.boxHeader}><Text>Customer</Text></View>
						{!!insp.companyName && keyval("Name", insp.companyName)}
						{!!insp.contactName && keyval("Contact", insp.contactName)}
						{!!insp.contactPhone && keyval("Phone", insp.contactPhone)}
						{!!insp.contactEmail && keyval("Email", insp.contactEmail)}
						{!!insp.contactAddress && keyval("Address", insp.contactAddress)}
					</>}
				</View>

				<View style={styles.inspectorBox}>
					<View style={styles.boxHeader}><Text>Inspected By</Text></View>
					{keyval("Name", insp.inspectorName)}
					{keyval("Inspector ID", insp.inspectorIdentifier)}
					{keyval("Company", insp.inspectorCompany)}
					<Image style={styles.userLogo} src={Api.forProfile(null).getUserLogoUrl(insp.userIdentifier, true)} />
				</View>
			</View>

			<View style={styles.generalBox}>
				<View style={styles.boxHeader}><Text>Equipment</Text></View>
				{keyval("Asset #", insp.componentAssetNumber)}
				{survey.surveyComponentTypes.map(sct => {
					let ic = insp.getInspectionComponentFor(sct.componentTypeId);
					return <React.Fragment key={ic.componentTypeId}>	
						<View><Text style={styles.componentType}>{sct.componentType.name}</Text></View>
						{(!!ic.modelNumber) && keyval("Model #", ic.modelNumber)}
						{(!!ic.serialNumber) && keyval("Serial #", ic.serialNumber)}
					</React.Fragment>;
				})}
				{!!insp.location && keyval("Location", insp.location)}
			</View>

			<View style={styles.generalBox}>
				<Image style={styles.legend} src={legendImage} />
				<View style={styles.boxHeader}><Text>Inspection Checks</Text></View>
				<View style={styles.questionShortContainer}>
					{allQuestions.filter(qmeta => qmeta.question.newSection !== true && !(isStringBlank(qmeta.question.name) && isStringBlank(qmeta.question.question))).map(qmeta => {
						let resp = insp.getResponseFor(qmeta.questionId);
						let respVal = resp.value;
						if(!respVal) {
							respVal = "SKIP";
						}
						if((!!resp.comments) || resp.photos.length > 0) {
							qExtendedCount += 1;
						}
						if(qmeta.question.commentsOnly) {
							return <React.Fragment key={qmeta.id}></React.Fragment>;
						}
						return <View style={styles.questionShort} key={qmeta.id}>
							<Image style={styles.checkImage} src={checkImages[respVal]} />
							<Text style={styles.checkName}>{qmeta.question.name}</Text>
						</View>;
					})}
				</View>
			</View>

			{qExtendedCount > 0 && <View style={styles.generalBox}>
				<View style={styles.boxHeader}><Text>Check Details</Text></View>
				{allQuestions.map(qmeta => {
					let resp = insp.getResponseFor(qmeta.questionId);
					let q = qmeta.question;
					if((!!resp.comments) || resp.photos.length > 0) {
						return <View style={styles.checkDetailContainer} key={qmeta.id}>
							{keyvalDetail(q.name, q.question)}
							{!!resp.comments && <>
								<Text style={styles.comments}>{resp.comments}</Text>
							</>}
							<View style={styles.vertSpacer}></View>
							<View style={styles.photoContainer}>
								{resp.photos.length > 0 && <View style={styles.imageList}>
									{resp.photos.map(ph => <Image style={styles.photoImage} src={ph.url || ph.photoData} key={ph.id} />)}
								</View>}
							</View>
						</View>;
					} else {
						return <React.Fragment key={qmeta.id}></React.Fragment>;
					}
				})}
			</View>}

			{insp.inspectionParts.length > 0 && <View style={styles.generalBox}>
				<View style={styles.boxHeader}><Text>Parts</Text></View>

				<View style={styles.partTable}>
					<View style={styles.partRowHeader} wrap={false}>
							<View style={styles.partNumber}><Text style={styles.tblHdr}>Part No.</Text></View>
							<View style={styles.partName}><Text style={styles.tblHdr}>Description</Text></View>
							<View style={styles.partStatus}><Text style={styles.tblHdr}>Status</Text></View>
							<View style={styles.partNotes}><Text style={styles.tblHdr}>Notes</Text></View>
					</View>
					{insp.inspectionParts.map(p => {
						let nameparts = [p.partNumber, p.name].filter(x => x);
						let name = nameparts.join(" / ")
						let atts = [];
						if(p.ordered) {
							atts.push("Ordered");
						}
						if(p.replaced) {
							atts.push("Replaced");
						}

						// Get status (if ordered and replaced just say replaced)
						let statusString = "";
						if(p.replaced) {
							statusString = "Replaced";
						} else {
							if(p.ordered) {
								statusString = "Ordered";
							}
						}

						let atttext = atts.length > 0 ? `(${atts.join(" / ")})` : "";
						return <View key={p.id} style={styles.partRow} wrap={false}>
							<View style={styles.partNumber}><Text style={styles.tblText}>{p.partNumber}</Text></View>
							<View style={styles.partName}><Text style={styles.tblText}>{p.name}</Text></View>
							<View style={styles.partOrdered}><Text style={styles.tblText}>{statusString}</Text></View>
							<View style={styles.partNotes}><Text style={styles.tblText}>{p.notes}</Text></View>
						</View>;
					})}
				</View>
			</View>}

			{insp.inspectionPhotos.length > 0 && <View style={styles.generalBox}>
				<View style={styles.boxHeader}><Text>General Photos</Text></View>
				<View style={styles.photoContainer}>
					{insp.inspectionPhotos.length > 0 && <View style={styles.imageList}>
						{insp.inspectionPhotos.map(ph => <Image style={styles.photoImage} src={ph.url || ph.photoData} key={ph.id} />)}
					</View>}
				</View>
			</View>}

			<View style={styles.generalBox}>
				<View style={styles.boxHeader}><Text>Final Approval</Text></View>
				{insp.completedAt && keyvalDetail("Completed At", <Timestamp dateString={insp.completedAt} formatter={dFormatter} />)}
				<View style={styles.vertSpacer}></View>
				{!!insp.comments && keyvalDetail("Notes", insp.comments)}
				<View style={styles.vertSpacer}></View>
				<View style={styles.sigContainer}>
					{insp.signature != null && <View style={styles.sigImageContainer}>
						<Image src={insp.signature.url || insp.signature.photoData} style={styles.sigImage} />
						<Text style={styles.sigImageText}>Signature</Text>
					</View>}
					<View style={styles.autogrow}></View>
					<View style={styles.vaultLogoContainer}>
						<Image style={styles.vaultLogo} src={vaultlogo} />
					</View>
				</View>
			</View>

			<View style={styles.footer} wrap={false}>
				<Image style={styles.footerLogo} src={mcelroyLogo} />
				<Text style={styles.footerText}>
					This inspection is built using a tool developed by McElroy Manufacturing, Inc.  
					The inspector listed in the form, not McElroy Manufacturing, is responsible for the content of this inspection.
				</Text>
			</View>
		</View>
	</>;
}